import React, { useContext } from 'react';
import { IModifierGroupContext, ModifierGroupContext } from '../../../../modifierGroupState/modifierGroupContext';
import { UserRoles, IItemModifierGroups } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { Link } from 'react-router-dom';
import { useLoggedUserRestaurants } from 'hooks/useLoggedUserRestaurants';
import Text from 'components/Text/Text';
import { RestaurantsAsLinkList } from 'components/Lists/ListView/RestaurantsAsLinkList';
import DataController from 'components/DataController/DataController';
import classNames from 'classnames';
import style from './Item.module.scss';

interface IProps {
	data: IItemModifierGroups;
	index: number;
}

export const Item: React.FC<IProps> = ({ data, index }) => {
	const { setIDForDeletion, setIDForAssignRestaurants, setIDForEditModifiersTemplate } =
		useContext<IModifierGroupContext>(ModifierGroupContext);
	const hasEditAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.RESTAURANT_MANAGER,
		UserRoles.TECHNICAL,
		UserRoles.WAITER,
		UserRoles.RKL_SUPPORT_MANAGER,
	]);
	const nameRowClasses = classNames(style.name, style.row);
	const numberOfRestaurantsRowClasses = classNames(style.restaurant, style.row);
	const articleNumberRowClasses = classNames(style.articleNumber, style.row);
	const actionRowClasses = classNames(style.action, style.row);

	const restaurants = data.restaurantsModifierGroups.map((restaurant) => ({
		id: restaurant.restaurantId,
		restaurantName: restaurant.restaurantName,
	}));
	const accessibleRestaurants = useLoggedUserRestaurants(restaurants);

	const editButtonElement = accessibleRestaurants.length ? (
		<div className={style.editItemWrapper}>
			<Link to={`/menu-v2/modifier-groups/edit?modifierGroupID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editMainInfo" size="s" />
			</Link>
			<div className={style.linkStyle} onClick={() => setIDForAssignRestaurants(data.id)}>
				<Text translationNameSpace="listing" translationKey="editRestaurantsList" size="s" />
			</div>
			<Link to={`/menu-v2/modifier-groups/edit-restaurant-properties?modifierGroupID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editRestaurantsProperties" size="s" />
			</Link>
			<div className={style.linkStyle} onClick={() => setIDForEditModifiersTemplate(data.id)}>
				<Text translationNameSpace="listing" translationKey="editModifiersTemplate" size="s" />
			</div>
		</div>
	) : (
		<div className={style.editItemWrapper}>
			<Link to={`/menu-v2/modifier-groups/edit?modifierGroupID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editMainInfo" size="s" />
			</Link>
			<div className={style.linkStyle} onClick={() => setIDForAssignRestaurants(data.id)}>
				<Text translationNameSpace="listing" translationKey="assignRestaurants" size="s" />
			</div>
		</div>
	);

	return (
		<div data-test={`Item-${index}`}>
			<div className={style.container}>
				<Text
					className={nameRowClasses}
					translationKey={data.name}
					withTooltip={true}
					data-test="modifierGroupName"
				/>
				<div className={numberOfRestaurantsRowClasses}>
					<RestaurantsAsLinkList restaurants={accessibleRestaurants} />
				</div>
				<Text
					className={articleNumberRowClasses}
					translationKey={data.articleNumber || '-'}
					withTooltip={true}
					data-test="articleNumber"
				/>
				<div className={actionRowClasses}>
					<DataController
						URLWithIDToInfo={`/menu-v2/modifier-groups/view?modifierGroupID=${data.id}`}
						customEditButton={hasEditAccess && editButtonElement}
						onClickDelete={() => setIDForDeletion(data.id)}
						size="smallBtn"
					/>
				</div>
			</div>
		</div>
	);
};
